import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, CustomSelect, Error, Label } from "../..";
import TreeSelect from "../../UI/TreeSelect";
import { Controller, useForm } from "react-hook-form";
import { getAxios, postAxios, putAxios } from "../../../Services/commonService";
import {
  API_FORM,
  API_GET_ROLE_OPTIONS,
  API_PERMISSION,
  API_ROLES,
  API_USER_TYPE_OPTIONS,
} from "../../../../utils/api/ApiConstant";
import { yupResolver } from "@hookform/resolvers/yup";
import { RolesModalSchema } from "../../../Schema/Configuration/FormSchema";
import { exportFormPermissionsCsv } from "../../../../utils/exportFormPermissionCsv";
import Icon from "../../../../assets/icons/Icon";
import { lowerFirstLetterUsingRegex } from "../../../helper/common";
import {
  sanitizeKeyInput,
  sanitizeNameInput,
} from "../../../../utils/inputUtils";

const RolesModal = ({ handleModalHide, rowData, fetchData }) => {
  const isEdit = !!rowData?.id;

  const [formData, setFormData] = useState(null);
  const [dropdownList, setDropdownList] = useState([]);
  const [selectedForms, setSelectedForms] = useState({});
  const [parentlistData, setParentlistData] = useState([]);
  const [permissionList, setPermissionList] = useState([]);
  // Added: userType list state
  const [userTypeList, setUserTypeList] = useState([]);

  // Changed: Single useEffect with parallel fetching instead of 3 separate ones
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [formsRes, permissionsRes, parentRes] = await Promise.all([
          getAxios(API_FORM),
          getAxios(API_PERMISSION),
          getAxios(API_GET_ROLE_OPTIONS),
        ]);

        setDropdownList(formsRes?.filter((item) => item.isPage === true) || []);

        setPermissionList(
          permissionsRes.map((permission) => ({
            label: permission?.name,
            value: permission?.id,
            key: permission?.key,
          })),
        );

        setParentlistData(parentRes);
      } catch (err) {
        console.error("Failed to fetch modal data:", err);
      }
    };

    fetchAllData();
  }, []);

  const findParentLabel = useCallback((parentId, tree) => {
    for (const node of tree) {
      if (node.value === parentId) {
        return node.label;
      }
      if (node.children && node.children.length > 0) {
        const found = findParentLabel(parentId, node.children);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Added: fetch user type list based on selected parent's userType
  const fetchUserTypeList = useCallback(async (userType) => {
    try {
      if (!userType) return;

      const res = await getAxios(API_USER_TYPE_OPTIONS, {
        userType: userType,
        self: true,
      });
      setUserTypeList(res);
    } catch (error) {
      console.log("🚀 ~ RolesModal ~ error:", error);
    }
  }, []);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    setError,
    formState: { errors, isSubmitting },
    watch, // Added: watch for parentId changes
  } = useForm({
    defaultValues: {
      name: "",
      key: "",
      parentId: null,
      userType: null, // Added: userType default value
      description: "",
      formPermissions: [],
      canAssignSelfRole: false,
    },
    resolver: yupResolver(RolesModalSchema),
  });

  // Added: watch parentId to trigger userType fetch
  const parentValue = watch("parentId");

  // Added: fetch user type list when parent selection changes
  useEffect(() => {
    if (!parentValue || !parentValue?.userType) return;

    fetchUserTypeList(parentValue?.userType);
  }, [parentValue]);

  // Added: set userType value in edit mode once userTypeList is loaded
  useEffect(() => {
    if (!isEdit || !rowData?.userType || !userTypeList) return;

    const match = userTypeList.find((x) => x?.value === rowData?.userType);

    if (!match) return;

    setValue("userType", match);
  }, [isEdit, rowData?.userType, userTypeList]);

  const buildFormPermissionsPayload = useCallback(
    () =>
      Object.entries(selectedForms)
        .filter(([_, value]) => value.permissions?.length > 0)
        .map(([formId, value]) => ({
          formId: Number(formId),
          permissions: value.permissions,
        })),
    [selectedForms],
  );

  // Build parent tree
  const parentIdList = useMemo(() => {
    const mapping = new Map();
    parentlistData?.forEach((el) => {
      mapping.set(el.value, {
        ...el,
        isDisabled: isEdit && el.value === rowData?.id,
        children: [],
      });
    });

    const rootNodes = [];
    parentlistData?.forEach((el) => {
      const node = mapping.get(el.value);
      const parent = mapping.get(el.ParentId);
      if (el.ParentId === null || !parent) {
        rootNodes.push(node);
      } else {
        parent.children.push(node);
      }
    });

    return rootNodes;
  }, [parentlistData, isEdit, rowData?.id]);

  // Changed: uses API_GET_ROLES_BY_ID and parentlistData.find() for parent lookup
  useEffect(() => {
    if (!isEdit || !parentlistData || !rowData?.id) return;

    const fetchRoleDetail = async () => {
      try {
        const response = await getAxios(`${API_ROLES}/${rowData.id}`);
        const role = response;

        if (!role) return;

        setFormData(role);

        const parentLabel = parentlistData.find(
          (x) => x?.value === role.parentId,
        );

        reset({
          name: role?.name || "",
          key: role?.key || "",
          description: role?.description || "",
          parentId: parentLabel || null,
          formPermissions: [],
          canAssignSelfRole: role?.canAssignSelfRole ?? false,
        });

        if (role?.formPermissions?.length > 0) {
          const grouped = role.formPermissions.reduce((acc, item) => {
            if (!acc[item.formId]) {
              acc[item.formId] = {
                permissions: [],
              };
            }
            acc[item.formId].permissions.push(item.permissionId);
            return acc;
          }, {});

          setSelectedForms(grouped);
        }
      } catch (err) {
        console.error("Failed to fetch role detail:", err);
      }
    };

    fetchRoleDetail();
  }, [isEdit, rowData?.id, parentlistData, reset]);

  const formMap = useMemo(() => {
    return dropdownList.reduce((acc, form) => {
      acc[form.id] = form;
      return acc;
    }, {});
  }, [dropdownList]);

  const getViewPermissionId = (form) => {
    return form.permissions?.find(
      (p) => p.permissionName?.trim().toLowerCase() === "view",
    )?.permissionId;
  };

  const normalizePermissions = useCallback((permissions, viewPermissionId) => {
    if (!viewPermissionId) {
      return permissions;
    }

    const uniquePermissions = [...new Set(permissions)];
    const nonViewPermissions = uniquePermissions.filter(
      (id) => id !== viewPermissionId,
    );

    if (
      nonViewPermissions.length > 0 &&
      !uniquePermissions.includes(viewPermissionId)
    ) {
      return [...uniquePermissions, viewPermissionId];
    }

    return uniquePermissions;
  }, []);

  // Handle permission toggle
  const handlePermissionToggle = (formId, permissionId, checked) => {
    const form = formMap[formId];

    if (!form) return;
    const viewPermissionId = getViewPermissionId(form);
    setSelectedForms((prev) => {
      const existing = prev[formId]?.permissions || [];
      let permissions;

      if (checked) {
        permissions = normalizePermissions(
          [...existing, permissionId],
          viewPermissionId,
        );
      } else {
        if (permissionId === viewPermissionId) {
          permissions = [];
        } else {
          const remainingPermissions = existing.filter(
            (id) => id !== permissionId,
          );
          permissions = normalizePermissions(
            remainingPermissions,
            viewPermissionId,
          );
        }
      }
      const updated = { ...prev };
      if (permissions.length > 0) {
        updated[formId] = { permissions };
      } else {
        delete updated[formId];
      }
      return updated;
    });
  };

  const handleTogglePermissionColumn = (permissionId, checked) => {
    setSelectedForms((prev) => {
      const updated = { ...prev };

      dropdownList.forEach((form) => {
        const hasPermission = form.permissions?.some(
          (p) => p.permissionId === permissionId,
        );

        if (!hasPermission) return;
        const viewPermissionId = getViewPermissionId(form);
        const existing = updated[form.id]?.permissions || [];
        let permissions;

        if (checked) {
          permissions = normalizePermissions(
            [...existing, permissionId],
            viewPermissionId,
          );
        } else {
          if (permissionId === viewPermissionId) {
            permissions = [];
          } else {
            const remainingPermissions = existing.filter(
              (id) => id !== permissionId,
            );
            permissions = normalizePermissions(
              remainingPermissions,
              viewPermissionId,
            );
          }
        }

        if (permissions.length > 0) {
          updated[form.id] = { permissions };
        } else {
          delete updated[form.id];
        }
      });
      return updated;
    });
  };

  const handleToggleAllPermissionsForForm = (formId, checked) => {
    const form = formMap[formId];
    if (!form) return;
    setSelectedForms((prev) => {
      const updated = { ...prev };
      if (checked) {
        updated[formId] = {
          permissions: form.permissions.map((p) => p.permissionId),
        };
      } else {
        delete updated[formId];
      }
      return updated;
    });
  };

  useEffect(() => {
    setValue("formPermissions", buildFormPermissionsPayload(), {
      shouldDirty: true,
    });
  }, [selectedForms, buildFormPermissionsPayload, setValue]);

  // Changed: added userType to submit payload
  const handleFormSubmit = async (payload) => {
    try {
      const base = {
        name: payload.name,
        key: payload.key,
        parentId: payload.parentId?.value || null,
        userType: payload.userType?.value || null, // Added
        description: payload.description,
        formPermissions: buildFormPermissionsPayload(),
        canAssignSelfRole: payload?.canAssignSelfRole,
      };

      let res;
      if (isEdit) {
        res = await putAxios(`${API_ROLES}/${rowData.id}`, {
          ...base,
          isActive: formData?.isActive,
          rowVersion: formData?.rowVersion,
        });
      } else {
        res = await postAxios(API_ROLES, base);
      }

      if (res?.success) {
        fetchData();
        handleModalHide();
      }
      res?.errors?.forEach((element) => {
        const field = lowerFirstLetterUsingRegex(element?.field);
        if (field)
          setError(field, {
            type: "manual",
            message: element.message,
          });
      });
    } catch (err) {
      console.error("Failed to submit form:", err);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <fieldset disabled={isSubmitting}>
        <div className="row m-0 p-0">
          {/* name */}
          <div className="col-lg-4 col-md-6 col-12">
            <div className="form-group">
              <Label htmlFor="name" className="form-label" isRequired>
                Name
              </Label>
              <input
                className="form-control"
                type="text"
                id="name"
                placeholder="Enter name"
                autoFocus={true}
                tabIndex={1}
                {...register("name", {
                  onChange: (e) => {
                    e.target.value = sanitizeNameInput(e.target.value);
                  },
                })}
              />
              {errors.name && <Error>{errors.name.message}</Error>}
            </div>
          </div>

          {/* key */}
          <div className="col-lg-4 col-md-6 col-12">
            <div className="form-group">
              <Label htmlFor="key" className="form-label" isRequired>
                Key
              </Label>
              <input
                className="form-control"
                type="text"
                id="key"
                placeholder="Enter key"
                tabIndex={2}
                {...register("key", {
                  onChange: (e) => {
                    e.target.value = sanitizeKeyInput(e.target.value);
                  },
                })}
              />
              {errors.key && <Error>{errors.key.message}</Error>}
            </div>
          </div>

          {/* parent */}
          <div className="col-lg-4 col-md-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="parentId"
                className="form-label"
                isRequired={true}
              >
                Parent
              </Label>
              <Controller
                name="parentId"
                control={control}
                render={({ field }) => (
                  <TreeSelect
                    options={parentIdList}
                    value={field.value}
                    // Changed: reset userType when parent changes
                    onChange={async (selectedOption) => {
                      field.onChange(selectedOption);
                      setValue("userType", null);
                    }}
                    isClearable
                    placeholder="Select parent..."
                    error={errors.parentId}
                    tabIndex={3}
                    disabled={field.disabled}
                  />
                )}
              />
              {errors.parentId && <Error>{errors.parentId.message}</Error>}
            </div>
          </div>

          {/* Added: User Type field */}
          <div className="col-lg-4 col-md-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="userType"
                className="form-label"
                isRequired={true}
              >
                User Type
              </Label>
              <Controller
                name="userType"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    options={userTypeList}
                    value={field.value}
                    onChange={(selected) => {
                      field.onChange(selected);
                    }}
                    onBlur={field.onBlur}
                    placeholder="Select User Type"
                    isMulti={false}
                  />
                )}
              />
              {errors.userType && <Error>{errors.userType.message}</Error>}
            </div>
          </div>

          {/* Changed: col-8 instead of col-12 */}
          <div className="col-lg-4 col-md-6 col-12">
            <div className="form-group">
              <Label htmlFor="description" className="form-label">
                Description
              </Label>
              <input
                className="form-control"
                type="text"
                id="description"
                placeholder="Enter description"
                {...register("description")}
                tabIndex={4}
              />
              {errors.description && (
                <Error>{errors.description.message}</Error>
              )}
            </div>
          </div>

          {/* can assign */}
          <div className="col-lg-4 col-md-6 col-12">
            <div className="form-group">
              <Label htmlFor="canAssignSelfRole" className="form-label">
                Can assign same role to another user?
              </Label>
              <div className="form-control d-flex justify-content-start gap-2"> 
              <div htmlFor="" className="form-switch form-switch-sm">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="cancanAssignSelfRole"
                  {...register("canAssignSelfRole")}
                />
              </div>
              <span>Yes / No</span>
              </div>
            </div>
          </div>
        </div>

        {/* Permissions Section */}
        <div className="permission-card">
          <div className="heading_model">
            <h5 className="section-title">
              Form Permissions <span className="text-danger">*</span>
            </h5>
            <Button
              type="button"
              className="btn btn_main px-2 py-2 mx-1"
              title="Export CSV"
              onClick={() =>
                exportFormPermissionsCsv(
                  selectedForms,
                  dropdownList,
                  permissionList,
                )
              }
            >
              <Icon
                name="FileSpreadsheet"
                className="icon_button"
                cursorPointer={true}
              />
              Export
            </Button>
          </div>

          <div className="table-responsive">
            <table className="report-card-box">
              <thead>
                <tr>
                  <th className="text-center"></th>
                  <th className="text-start">Form Name</th>
                  {permissionList.map((permission) => (
                    <th key={permission.value} className="text-center">
                      {permission.label}
                    </th>
                  ))}
                </tr>
                <tr className="tr_hading">
                  <th className="text-center">All</th>
                  <th className="text-center"></th>
                  {permissionList.map((permission) => (
                    <th key={permission.value} className="text-center">
                      <label className="form-switch form-switch-sm">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={(() => {
                            const formsWithPermission = dropdownList.filter(
                              (form) =>
                                form.permissions?.some(
                                  (p) => p.permissionId === permission.value,
                                ),
                            );

                            return (
                              formsWithPermission.length > 0 &&
                              formsWithPermission.every((form) =>
                                selectedForms[form.id]?.permissions?.includes(
                                  permission.value,
                                ),
                              )
                            );
                          })()}
                          onChange={(e) =>
                            handleTogglePermissionColumn(
                              permission.value,
                              e.target.checked,
                            )
                          }
                        />
                      </label>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dropdownList.map((form) => {
                  const selected = selectedForms[form.id];

                  return (
                    <tr
                      key={form.id}
                      className={` ${selected?.checked ? "" : ""}`}
                    >
                      <td className="text-center">
                        <label className="form-switch form-switch-sm">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={
                              form.permissions?.length > 0 &&
                              form.permissions.every((p) =>
                                selected?.permissions?.includes(p.permissionId),
                              )
                            }
                            onChange={(e) =>
                              handleToggleAllPermissionsForForm(
                                form.id,
                                e.target.checked,
                              )
                            }
                          />
                        </label>
                      </td>
                      <td className="text-start">
                        <strong>{form.name}</strong>
                      </td>

                      {permissionList.map((uniquePerm) => {
                        const permission = form.permissions?.find(
                          (p) => p.permissionId === uniquePerm.value,
                        );

                        if (!permission) {
                          return (
                            <td
                              key={uniquePerm.value}
                              className="text-center"
                            ></td>
                          );
                        }

                        const isViewPermission =
                          permission.permissionName?.toLowerCase() === "view";

                        const isChecked =
                          selected?.permissions?.includes(
                            permission.permissionId,
                          ) || false;

                        return (
                          <td
                            key={permission.permissionId}
                            className="text-center"
                          >
                            <label className="form-switch form-switch-sm">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                disabled={false}
                                checked={isChecked}
                                onChange={(e) =>
                                  handlePermissionToggle(
                                    form.id,
                                    permission.permissionId,
                                    e.target.checked,
                                  )
                                }
                                title={
                                  isViewPermission
                                    ? "View permission is always enabled"
                                    : ""
                                }
                              />
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {errors.formPermissions && (
            <Error>{errors.formPermissions.message}</Error>
          )}
        </div>

        <div className="d-flex justify-content-end w-100">
          <Button
            type="button"
            className="btn btn_main btn_muted px-3 py-2 mx-1"
            onClick={handleModalHide}
            tabIndex={6}
          >
            Close
          </Button>
          <Button
            type="submit"
            className="btn btn_main px-3 py-2 mx-1"
            tabIndex={5}
            isDisabled={isSubmitting}
          >
            {isSubmitting ? "Loading.." : isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </fieldset>
    </form>
  );
};

export default RolesModal;
